import React, { useEffect, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  bulkUploadGoals,
  createGoal,
} from "../../../redux/features/appraisalSlice";
import { use } from "react";
import { ta } from "date-fns/locale";
import dayjs from "dayjs";
const BulkUploadForm = ({ dispatchdata }) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.appraisal);
  const [excellConvertionLoading, setExcellConvertionLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");

  const ValidationSchema = Yup.object().shape({
    excelData: Yup.array().min(1, "Please upload at least one row in Excel"),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      excelData: [],
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const data = values.excelData.map((item) => ({
        ...item,
      }));

      dispatch(bulkUploadGoals(data)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar("Goals uploaded successfully", {
            variant: "success",
          });
          setFileName("");
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: "error",
          });
        }
      });
    },
  });

  const handleCancel = () => {
    setOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    formik.resetForm();
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setExcellConvertionLoading(true);
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const binaryString = e.target.result;
          const workbook = XLSX.read(binaryString, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          const formatDate = (value) => {
            if (!value) return "";

            if (typeof value === "number") {
              const excelEpoch = new Date(Date.UTC(1899, 11, 30));
              const jsDate = new Date(excelEpoch.getTime() + value * 86400000);
              return dayjs(jsDate).format("YYYY-MM-DD");
            }

            const parsed = dayjs(value);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
          };

          const mergedGoals = [];
          let currentGoal = null;

          rawData.forEach((row) => {
            const goalName = row["Goal Name"]?.trim();
            const jobName = row["Job Name"]?.trim();
            const startDate = formatDate(row["Start Date"]);
            const endDate = formatDate(row["End Date"]);
            const jobStartDate = formatDate(row["Job Start Date"]);
            const jobEndDate = formatDate(row["Job End Date"]);
            const desc = row["Description"]?.trim();
            const userId = row["User ID"];
            const approvalStatus = row["Approval Status"]?.trim();
            const approvedUserId = row["Approved User ID"];
            const approvalTimestamp = formatDate(row["Approval Time Stamp"]);
            const isArchived = row["Is Archived"]?.trim();
            const completedTimeStamp = formatDate(row["Completed Time Stamp"]);

            if (goalName) {
              // New goal starts
              currentGoal = {
                goal_name: goalName,
                start_date: startDate,
                end_date: endDate,
                description: desc,
                user_id: userId,
                approval_status: approvalStatus,
                approved_user_id: approvedUserId,
                approval_timestamp: approvalTimestamp,
                is_archived: isArchived,
                completed_timestamp: completedTimeStamp,
                jobs: [],
                tagIds: [],
              };
              mergedGoals.push(currentGoal);
            }

            if (!currentGoal) return;

            if (jobName) {
              currentGoal.jobs.push({
                job_name: jobName,
                start_date: jobStartDate,
                end_date: jobEndDate,
                completed_timestamp: completedTimeStamp,
              });
            }

            if (row["Tag Names"]) {
              currentGoal.tagIds.push(row["Tag Names"].trim());
            }
          });

          formik.setFieldValue("excelData", mergedGoals);
        } catch (error) {
          console.error("Error parsing Excel file:", error);
          formik.setFieldValue("excelData", []);
        } finally {
          setExcellConvertionLoading(false);
        }
      };

      reader.onerror = () => {
        console.error("File reading error");
        setExcellConvertionLoading(false);
      };

      reader.readAsBinaryString(file);
    }
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <Box>
            <Stack direction={"column"} spacing={2}>
              <label htmlFor="ex" style={{ cursor: "pointer" }}>
                <Box
                  component="span"
                  sx={{
                    cursor: "pointer",
                    padding: "6px 12px",
                    background:
                      "linear-gradient(to right,rgba(53, 118, 222, 1),rgba(36, 71, 199, 1) 100%)",
                    color: "white",
                    width: "200px",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "5px",
                  }}
                >
                  <Icon icon="mdi:file-import" width={20} height={20} />
                  Import Excel
                </Box>
              </label>
              <TextField
                name="excelData"
                inputRef={fileInputRef}
                InputLabelProps={{ shrink: true }}
                placeholder="Select an Excel File to Upload"
                variant="outlined"
                id="ex"
                type="file"
                sx={{ display: "none" }}
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                error={
                  formik.touched.excelData && Boolean(formik.errors.excelData)
                }
                helperText={
                  formik.touched.excelData && formik.errors.excelData
                    ? typeof formik.errors.excelData === "string"
                      ? formik.errors.excelData
                      : "Invalid data in Excel"
                    : ""
                }
              />
              {fileName && (
                <Typography variant="body1" color="black">
                  {fileName}
                </Typography>
              )}
            </Stack>
            {formik.errors.excelData && (
              <Typography variant="body2" color="error">
                {formik.errors.excelData}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-start", pt: 2 }}>
              {fileName && (
                <Button
                  disabled={loading || excellConvertionLoading}
                  size="small"
                  type="submit"
                  variant="contained"
                  color="secondary"
                >
                  {loading ? "Please wait..." : "Save"}
                </Button>
              )}
            </Box>
          </Box>
        </Form>
      </FormikProvider>
    </>
  );
};

export default BulkUploadForm;
